// AssinaturaView — aluno vê plano atual da plataforma + faz upgrade.
//
// Diferente da Forma de Pagamento (que lista cartões + assinaturas detalhadas),
// aqui o foco é o conceito de plano:
//   - Card grande mostrando o plano ativo (default: Free)
//   - Lista de outros planos disponíveis com botão "Fazer upgrade"
//   - Ao clicar upgrade: modal pedindo Pix vs Cartão → cria subscription
//
// Cobranças mensais subsequentes são processadas pelo cron — o aluno gerencia
// método de pagamento em "Forma de pagamento".

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  Copy,
  CreditCard,
  Loader2,
  QrCode,
  BadgeDollarSign,
  X
} from "lucide-react";
import { generateQrWithLogoSvg } from "@app/ui/lib/qr-with-logo";
import { Skeleton } from "@app/ui/components/skeleton";
import { openAlert } from "./ConfirmPromptModal";

interface PlanFeatures {
  ai?: boolean;
  exportPdf?: boolean;
  maxBanks?: number | null;
  maxProvas?: number | null;
  [k: string]: unknown;
}

interface Plan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  monthlyCents: number;
  features: PlanFeatures;
  isActive: boolean;
  displayOrder: number;
}

interface SubscriptionRow {
  id: string;
  platformPlanSlug: string | null;
  valorCents: number;
  billingType: "pix_monthly" | "credit_card";
  status: "active" | "paused" | "past_due" | "canceled";
  nextChargeAt: string;
  createdAt: string;
}

interface Response {
  plans: Plan[];
  current: Plan | null;
  subscription: SubscriptionRow | null;
}

const phase = signal<"loading" | "ready" | "error">("loading");
const planData = signal<Response | null>(null);
const errMsg = signal<string | null>(null);
const upgradingTo = signal<Plan | null>(null);
// Quando upgrade Pix gera brcode mês-1, abre modal pra mostrar o QR.
const justGeneratedPix = signal<{
  pixBrcode: string;
  amountCents: number;
  planName: string;
} | null>(null);

function brl(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

async function callApi<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const e = body as { error?: { message?: string } } | null;
    throw new Error(e?.error?.message ?? `HTTP ${res.status}`);
  }
  return body as T;
}

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    planData.value = await callApi<Response>("/api/student/platform-plan");
    phase.value = "ready";
  } catch (e) {
    errMsg.value = e instanceof Error ? e.message : String(e);
    phase.value = "error";
  }
}

export function AssinaturaView({ embedded = false }: { embedded?: boolean } = {}) {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-3">
        <Skeleton class="h-40 w-full" />
        <Skeleton class="h-32 w-full" />
      </div>
    );
  }
  if (phase.value === "error" || !planData.value) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value ?? "Erro ao carregar."}
      </div>
    );
  }

  const d = planData.value;
  const current = d.current;
  const others = d.plans.filter((p) => p.slug !== (current?.slug ?? "free"));

  return (
    <div class="space-y-6">
      {!embedded && (
        <header>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <BadgeDollarSign className="size-6 text-primary" />
            Plano da plataforma
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Seu plano atual e opções de upgrade. Cobranças mensais e cartão de
            crédito ficam na seção "Forma de pagamento".
          </p>
        </header>
      )}

      {current && <CurrentPlanCard plan={current} sub={d.subscription} />}

      {others.length > 0 && (
        <section class="space-y-3">
          <h2 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Outros planos disponíveis
          </h2>
          <div class="grid gap-3 md:grid-cols-2">
            {others.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                isCurrent={false}
                onUpgrade={() => (upgradingTo.value = p)}
              />
            ))}
          </div>
        </section>
      )}

      {upgradingTo.value && (
        <UpgradeModal
          plan={upgradingTo.value}
          onClose={() => (upgradingTo.value = null)}
        />
      )}

      {justGeneratedPix.value && (
        <PixModal
          pixBrcode={justGeneratedPix.value.pixBrcode}
          amountCents={justGeneratedPix.value.amountCents}
          planName={justGeneratedPix.value.planName}
          onClose={() => (justGeneratedPix.value = null)}
        />
      )}
    </div>
  );
}

function PixModal({
  pixBrcode,
  amountCents,
  planName,
  onClose
}: {
  pixBrcode: string;
  amountCents: number;
  planName: string;
  onClose: () => void;
}) {
  const [qrSvg, setQrSvg] = useState<string>("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    generateQrWithLogoSvg(pixBrcode, {
      margin: 1,
      dark: "#0f172a",
      light: "#ffffff"
    })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg("");
      });
    return () => {
      cancelled = true;
    };
  }, [pixBrcode]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(pixBrcode);
    } catch {
      // ignora — alguns browsers exigem gesture
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div class="w-full max-w-md rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <QrCode className="size-4 text-emerald-600" />
            Pague o primeiro mês
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="space-y-3 p-5">
          <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-center">
            <p class="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
              Plano {planName}
            </p>
            <p class="mt-1 text-2xl font-bold tabular-nums">
              {(amountCents / 100).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL"
              })}
            </p>
          </div>

          {qrSvg ? (
            <div
              class="mx-auto size-[240px] overflow-hidden rounded-md bg-white p-3 shadow-sm [&_svg]:size-full"
              dangerouslySetInnerHTML={{ __html: qrSvg }}
              aria-label="QR Code Pix"
            />
          ) : (
            <div class="mx-auto grid size-[240px] place-items-center rounded-md bg-muted text-xs text-muted-foreground">
              Gerando QR…
            </div>
          )}

          <div>
            <div class="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Pix copia e cola
            </div>
            <div class="flex items-center gap-1 rounded-md border border-border bg-background">
              <code class="min-w-0 flex-1 truncate px-2 py-1.5 font-mono text-[10px]">
                {pixBrcode}
              </code>
              <button
                type="button"
                onClick={copy}
                title={copied ? "Copiado!" : "Copiar"}
                aria-label="Copiar Pix copia e cola"
                class={`grid size-8 shrink-0 place-items-center rounded-r-md border-l border-border transition-colors ${
                  copied
                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              </button>
            </div>
          </div>

          <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-800 dark:text-amber-300">
            Você receberá um novo Pix automaticamente todo mês. Os próximos
            códigos aparecem em <strong>Notificações</strong>. Pra mudar pra
            cartão, vá em "Forma de pagamento".
          </div>
        </div>
      </div>
    </div>
  );
}

function CurrentPlanCard({
  plan,
  sub
}: {
  plan: Plan;
  sub: SubscriptionRow | null;
}) {
  return (
    <section class="rounded-md border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 p-5">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <p class="text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            Seu plano
          </p>
          <h2 class="text-2xl font-bold">{plan.name}</h2>
          {plan.description && (
            <p class="mt-1 text-sm text-muted-foreground">{plan.description}</p>
          )}
        </div>
        <div class="text-right">
          <p class="text-3xl font-bold tabular-nums">
            {plan.monthlyCents === 0 ? (
              <span class="text-emerald-600 dark:text-emerald-400">Grátis</span>
            ) : (
              brl(plan.monthlyCents)
            )}
          </p>
          {plan.monthlyCents > 0 && (
            <p class="text-[10px] text-muted-foreground">por mês</p>
          )}
        </div>
      </div>

      <div class="mt-4 flex flex-wrap gap-1.5">
        <FeatureChips features={plan.features} />
      </div>

      {sub && (
        <div class="mt-4 border-t border-violet-500/20 pt-3 text-xs">
          <div class="flex flex-wrap gap-x-4 gap-y-1">
            <span>
              <strong>Cobrança:</strong>{" "}
              {sub.billingType === "credit_card" ? "Cartão de crédito" : "Pix mensal"}
            </span>
            <span>
              <strong>Próximo ciclo:</strong>{" "}
              <span class="tabular-nums">
                {new Date(sub.nextChargeAt).toLocaleDateString("pt-BR")}
              </span>
            </span>
            <span>
              <strong>Status:</strong>{" "}
              <StatusLabel status={sub.status} />
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

function PlanCard({
  plan,
  isCurrent,
  onUpgrade
}: {
  plan: Plan;
  isCurrent: boolean;
  onUpgrade?: () => void;
}) {
  const free = plan.monthlyCents === 0;
  return (
    <div class="rounded-md border border-border bg-card p-5">
      <div class="flex items-start justify-between gap-3">
        <h3 class="text-lg font-bold">{plan.name}</h3>
        <div class="text-right">
          <p class="text-2xl font-bold tabular-nums">
            {free ? (
              <span class="text-emerald-600">Grátis</span>
            ) : (
              brl(plan.monthlyCents)
            )}
          </p>
          {!free && <p class="text-[10px] text-muted-foreground">/mês</p>}
        </div>
      </div>
      {plan.description && (
        <p class="mt-1 text-xs text-muted-foreground">{plan.description}</p>
      )}
      <div class="mt-3 flex flex-wrap gap-1.5">
        <FeatureChips features={plan.features} />
      </div>
      {!isCurrent && onUpgrade && !free && (
        <button
          type="button"
          onClick={onUpgrade}
          class="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
        >
          Fazer upgrade
        </button>
      )}
    </div>
  );
}

function FeatureChips({ features }: { features: PlanFeatures }) {
  const items: { label: string; on: boolean }[] = [];
  if (features.ai !== undefined) {
    items.push({ label: "Assistente IA", on: !!features.ai });
  }
  if (features.exportPdf !== undefined) {
    items.push({ label: "Exportar PDF", on: !!features.exportPdf });
  }
  if (features.maxBanks !== undefined) {
    items.push({
      label: `Bancos: ${features.maxBanks === null ? "ilimitado" : features.maxBanks}`,
      on: true
    });
  }
  if (features.maxProvas !== undefined) {
    items.push({
      label: `Provas: ${features.maxProvas === null ? "ilimitado" : features.maxProvas}`,
      on: true
    });
  }
  return (
    <>
      {items.map((c) => (
        <span
          key={c.label}
          class={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
            c.on
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {c.on && <Check className="size-3" />}
          {c.label}
        </span>
      ))}
    </>
  );
}

function StatusLabel({ status }: { status: SubscriptionRow["status"] }) {
  if (status === "active") return <span class="text-emerald-600">ativa</span>;
  if (status === "paused") return <span class="text-amber-600">pausada</span>;
  if (status === "past_due") return <span class="text-destructive">em atraso</span>;
  return <span class="text-muted-foreground">cancelada</span>;
}

function UpgradeModal({ plan, onClose }: { plan: Plan; onClose: () => void }) {
  const [billingType, setBillingType] = useState<"pix_monthly" | "credit_card">(
    "credit_card"
  );
  const [submitting, setSubmitting] = useState(false);

  async function confirm() {
    setSubmitting(true);
    try {
      const r = await callApi<{
        pixCharge: { pixBrcode: string; pixTxid: string } | null;
        pixWarning?: string;
      }>("/api/student/platform-plan", {
        method: "POST",
        body: JSON.stringify({ slug: plan.slug, billingType })
      });
      onClose();
      await load();
      if (billingType === "credit_card") {
        void openAlert({
          title: "Assinatura criada!",
          message: `Você assinou o plano ${plan.name}. Pra concluir, cadastre o cartão de crédito em "Forma de pagamento".`
        });
      } else if (r.pixCharge) {
        // Mostra o QR do mês 1 imediatamente.
        justGeneratedPix.value = {
          pixBrcode: r.pixCharge.pixBrcode,
          amountCents: plan.monthlyCents,
          planName: plan.name
        };
      } else {
        void openAlert({
          title: "Assinatura criada — Pix pendente",
          message:
            r.pixWarning ??
            "Não foi possível gerar o Pix agora. O admin precisa configurar o Pix da plataforma."
        });
      }
    } catch (e) {
      void openAlert({
        title: "Erro ao criar assinatura",
        message: e instanceof Error ? e.message : String(e)
      });
    } finally {
      setSubmitting(false);
    }
  }

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div class="w-full max-w-md rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <h2 class="text-base font-bold">Confirmar assinatura</h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="space-y-4 p-5">
          <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-4 text-center">
            <p class="text-[10px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              Plano {plan.name}
            </p>
            <p class="mt-1 text-3xl font-bold tabular-nums">
              {brl(plan.monthlyCents)}
              <span class="ml-1 text-xs font-normal text-muted-foreground">
                /mês
              </span>
            </p>
          </div>

          <div>
            <label class="mb-2 block text-xs font-medium text-muted-foreground">
              Como você quer pagar todo mês?
            </label>
            <div class="grid grid-cols-2 gap-2">
              <PayChoice
                active={billingType === "credit_card"}
                icon={CreditCard}
                title="Cartão de crédito"
                desc="Cobrança automática"
                onClick={() => setBillingType("credit_card")}
              />
              <PayChoice
                active={billingType === "pix_monthly"}
                icon={QrCode}
                title="Pix mensal"
                desc="Você recebe novo QR todo mês"
                onClick={() => setBillingType("pix_monthly")}
              />
            </div>
          </div>

          <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-800 dark:text-amber-300">
            {billingType === "credit_card" ? (
              <>
                Após confirmar, cadastre seu cartão na seção "Forma de
                pagamento" pra a próxima cobrança ser feita automaticamente.
              </>
            ) : (
              <>
                Todo mês você recebe um novo Pix neste app. Se não pagar em
                até 7 dias, a assinatura entra em atraso.
              </>
            )}
          </div>

          <div class="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              class="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={submitting}
              class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {submitting && <Loader2 className="size-4 animate-spin" />}
              Confirmar assinatura
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PayChoice({
  active,
  icon: Icon,
  title,
  desc,
  onClick
}: {
  active: boolean;
  icon: typeof CreditCard;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`text-left rounded-md border p-3 transition-colors ${
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/40"
          : "border-border bg-background hover:border-primary/40"
      }`}
    >
      <Icon
        className={`size-5 ${active ? "text-primary" : "text-muted-foreground"}`}
      />
      <div class="mt-2 text-sm font-medium">{title}</div>
      <p class="text-[10px] text-muted-foreground">{desc}</p>
    </button>
  );
}
